import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { supabase } from "@/lib/supabase";
import { logActivity } from "@/lib/activityLog";
import {
  MergeMember,
  LinkCounts,
  detectDuplicateGroups,
  defaultSelection,
  pickKeptMember,
  isStuckMerged,
  isProperlyMerged,
  groupHasIncompleteMerge,
  fieldValue,
  combineListValues,
  snapshotMember,
} from "@/lib/memberMerge";
import MergeModal from "./MergeModal";
import OriginalSnapshotModal from "./OriginalSnapshotModal";

export interface MemberMergeToolHandle {
  openForMember: (memberId: string | number) => boolean;
}

interface LinkRow {
  id: number;
  member_id: number;
}

interface FetchedLinks {
  regs: LinkRow[];
  banners: LinkRow[];
  waitlist: LinkRow[];
  testimonials: LinkRow[];
}

interface Props {
  members: MergeMember[];
  onToast: (type: "success" | "error" | "info", message: string) => void;
  onRefresh: () => void;
}

const EMPTY_LINKS: FetchedLinks = { regs: [], banners: [], waitlist: [], testimonials: [] };

const MemberMergeTool = forwardRef<MemberMergeToolHandle, Props>(function MemberMergeTool(
  { members, onToast, onRefresh },
  ref,
) {
  const [links, setLinks] = useState<FetchedLinks>(EMPTY_LINKS);
  const [activeGroup, setActiveGroup] = useState<MergeMember[] | null>(null);
  const [selection, setSelection] = useState<Record<string, string>>({});
  const [keptId, setKeptId] = useState<string>("");
  const [busy, setBusy] = useState(false);
  const [viewingOriginal, setViewingOriginal] = useState<MergeMember | null>(null);
  const [showRecentMerges, setShowRecentMerges] = useState(false);

  const groups = useMemo(() => detectDuplicateGroups(members), [members]);
  const properMerged = useMemo(() => members.filter(isProperlyMerged), [members]);
  const stuckMerged = useMemo(() => members.filter(isStuckMerged), [members]);

  // Busca os vínculos (inscrições, banners, lista de aviso, depoimentos) de todos
  // os envolvidos nos grupos de duplicados e nas mesclagens já feitas.
  useEffect(() => {
    const ids = new Set<string>();
    groups.forEach((g) => g.forEach((m) => ids.add(String(m.id))));
    properMerged.forEach((m) => ids.add(String(m.id)));
    stuckMerged.forEach((m) => ids.add(String(m.id)));

    if (ids.size === 0) {
      setLinks(EMPTY_LINKS);
      return;
    }

    const idList = Array.from(ids);
    let cancelled = false;

    (async () => {
      const [regsRes, bannersRes, waitlistRes, testimonialsRes] = await Promise.all([
        supabase.from("event_registrations").select("id, member_id").in("member_id", idList),
        supabase.from("banners").select("id, member_id").in("member_id", idList),
        supabase.from("event_waitlist").select("id, member_id").in("member_id", idList),
        supabase.from("testimonials").select("id, member_id").in("member_id", idList),
      ]);

      if (cancelled) return;
      setLinks({
        regs: (regsRes.data || []) as LinkRow[],
        banners: (bannersRes.data || []) as LinkRow[],
        waitlist: (waitlistRes.data || []) as LinkRow[],
        testimonials: (testimonialsRes.data || []) as LinkRow[],
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [groups, properMerged, stuckMerged]);

  const linkCounts = useMemo(() => {
    const counts: Record<string, LinkCounts> = {};
    members.forEach((m) => {
      const key = String(m.id);
      counts[key] = {
        regs: links.regs.filter((r) => String(r.member_id) === key).length,
        banners: links.banners.filter((b) => String(b.member_id) === key).length,
        waitlist: links.waitlist.filter((w) => String(w.member_id) === key).length,
        testimonials: links.testimonials.filter((t) => String(t.member_id) === key).length,
      };
    });
    return counts;
  }, [links, members]);

  function linkSummary(m: MergeMember): string {
    const c = linkCounts[String(m.id)] || { regs: 0, banners: 0, waitlist: 0, testimonials: 0 };
    const parts: string[] = [];
    if (c.regs) parts.push(`${c.regs} inscrição${c.regs > 1 ? "ões" : ""} em evento`);
    if (c.banners) parts.push(`${c.banners} anúncio${c.banners > 1 ? "s" : ""}`);
    if (c.waitlist) parts.push(`${c.waitlist} na lista de aviso`);
    if (c.testimonials) parts.push(`${c.testimonials} depoimento${c.testimonials > 1 ? "s" : ""}`);
    return parts.length ? parts.join(", ") : "sem vínculos";
  }

  function openMerge(group: MergeMember[]) {
    const kept = pickKeptMember(group);
    setKeptId(String(kept.id));
    setSelection(defaultSelection(group));
    setActiveGroup(group);
  }

  useImperativeHandle(
    ref,
    () => ({
      openForMember: (memberId: string | number) => {
        const group = groups.find((g) => g.some((m) => String(m.id) === String(memberId)));
        if (group) {
          openMerge(group);
          return true;
        }
        return false;
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [groups],
  );

  async function handleConfirmMerge() {
    if (!activeGroup) return;
    setBusy(true);

    try {
      const kept = activeGroup.find((m) => String(m.id) === keptId);
      const others = activeGroup.filter((m) => String(m.id) !== keptId);
      if (!kept || others.length === 0) {
        onToast("error", "Nenhum cadastro duplicado para mesclar.");
        setBusy(false);
        return;
      }

      const byId = new Map(activeGroup.map((m) => [String(m.id), m]));
      const get = (key: string) => byId.get(selection[key]);

      const phoneMember = get("phone");
      const businessOn = get("has_physical_business")?.has_physical_business ?? kept.has_physical_business;

      // Campos de lista: "combine" junta tudo; senão usa o lado escolhido.
      const listValue = (key: string): string => {
        const sel = selection[key];
        if (sel === "combine") {
          return combineListValues(activeGroup.map((m) => fieldValue(m, key)));
        }
        const chosen = byId.get(sel);
        return (chosen ? fieldValue(chosen, key) : "") || fieldValue(kept, key);
      };

      const finalValues: Record<string, unknown> = {
        full_name: get("full_name")?.full_name ?? kept.full_name,
        email: get("email")?.email ?? kept.email,
        country_code: phoneMember?.country_code ?? kept.country_code,
        whatsapp: phoneMember?.whatsapp ?? kept.whatsapp,
        city_state: get("city_state")?.city_state ?? kept.city_state,
        profession: get("profession")?.profession ?? kept.profession,
        description: get("description")?.description ?? kept.description,
        bio_long: get("bio_long")?.bio_long ?? kept.bio_long,
        photo: get("photo")?.photo ?? kept.photo,
        photo_position: get("photo_position")?.photo_position ?? kept.photo_position ?? "top",
        instagram: get("instagram")?.instagram ?? kept.instagram,
        website: get("website")?.website ?? kept.website,
        linkedin: get("linkedin")?.linkedin ?? kept.linkedin,
        facebook: get("facebook")?.facebook ?? kept.facebook,
        tiktok: get("tiktok")?.tiktok ?? kept.tiktok,
        skills: listValue("skills"),
        languages: listValue("languages"),
        education: listValue("education"),
        services: listValue("services"),
        experience_years: get("experience_years")?.experience_years ?? kept.experience_years,
        has_physical_business: businessOn,
        business_name: businessOn ? (get("business_name")?.business_name ?? kept.business_name ?? null) : null,
        business_address: businessOn ? (get("business_address")?.business_address ?? kept.business_address ?? null) : null,
        business_city: businessOn ? (get("business_city")?.business_city ?? kept.business_city ?? null) : null,
        business_state: businessOn ? (get("business_state")?.business_state ?? kept.business_state ?? null) : null,
        business_zip: businessOn ? (get("business_zip")?.business_zip ?? kept.business_zip ?? null) : null,
      };

      const { error: updateErr } = await supabase
        .from("members")
        .update(finalValues)
        .eq("id", kept.id);
      if (updateErr) throw new Error(updateErr.message);

      for (const other of others) {
        const otherKey = String(other.id);
        const theirRegs = links.regs.filter((r) => String(r.member_id) === otherKey).map((r) => r.id);
        const theirBanners = links.banners.filter((b) => String(b.member_id) === otherKey).map((b) => b.id);
        const theirWaitlist = links.waitlist.filter((w) => String(w.member_id) === otherKey).map((w) => w.id);
        const theirTestimonials = links.testimonials
          .filter((t) => String(t.member_id) === otherKey)
          .map((t) => t.id);

        if (theirRegs.length) {
          await supabase.from("event_registrations").update({ member_id: kept.id }).in("id", theirRegs);
        }
        if (theirBanners.length) {
          await supabase.from("banners").update({ member_id: kept.id }).in("id", theirBanners);
        }
        if (theirWaitlist.length) {
          await supabase.from("event_waitlist").update({ member_id: kept.id }).in("id", theirWaitlist);
        }
        if (theirTestimonials.length) {
          await supabase.from("testimonials").update({ member_id: kept.id }).in("id", theirTestimonials);
        }

        const { error: markErr } = await supabase
          .from("members")
          .update({
            status: "merged",
            merged_into_id: kept.id,
            merged_links: {
              from_status: other.status,
              event_registrations: theirRegs,
              banners: theirBanners,
              event_waitlist: theirWaitlist,
              testimonials: theirTestimonials,
              snapshot: snapshotMember(other),
              merged_at: new Date().toISOString(),
            },
          })
          .eq("id", other.id);
        if (markErr) throw new Error(markErr.message);
      }

      await logActivity("member_merge", {
        kept_member_id: kept.id,
        kept_member_name: kept.full_name,
        merged_member_ids: others.map((o) => o.id),
        merged_member_names: others.map((o) => o.full_name),
      });

      onToast("success", `Mesclagem concluída! ${others.length} cadastro(s) unido(s) a ${kept.full_name}.`);
      setActiveGroup(null);
      onRefresh();
    } catch (err) {
      onToast("error", `Falha ao mesclar: ${(err as Error).message || "erro inesperado"}`);
    } finally {
      setBusy(false);
    }
  }

  async function handleUndo(merged: MergeMember) {
    if (!merged.merged_into_id) return;
    setBusy(true);

    try {
      const snapshot = (merged.merged_links || {}) as Record<string, unknown>;
      const regIds = (snapshot.event_registrations as number[]) || [];
      const bannerIds = (snapshot.banners as number[]) || [];
      const waitlistIds = (snapshot.event_waitlist as number[]) || [];
      const testimonialIds = (snapshot.testimonials as number[]) || [];
      const fromStatus = (snapshot.from_status as string) || "active";

      if (regIds.length) {
        await supabase.from("event_registrations").update({ member_id: merged.id }).in("id", regIds);
      }
      if (bannerIds.length) {
        await supabase.from("banners").update({ member_id: merged.id }).in("id", bannerIds);
      }
      if (waitlistIds.length) {
        await supabase.from("event_waitlist").update({ member_id: merged.id }).in("id", waitlistIds);
      }
      if (testimonialIds.length) {
        await supabase.from("testimonials").update({ member_id: merged.id }).in("id", testimonialIds);
      }

      const { error } = await supabase
        .from("members")
        .update({ status: fromStatus, merged_into_id: null, merged_links: null })
        .eq("id", merged.id);
      if (error) throw new Error(error.message);

      await logActivity("member_unmerge", {
        member_id: merged.id,
        member_name: merged.full_name,
        unmerged_from_id: merged.merged_into_id,
      });

      onToast("success", `Mesclagem desfeita: ${merged.full_name} voltou a ser ${fromStatus}.`);
      onRefresh();
    } catch (err) {
      onToast("error", `Falha ao desfazer: ${(err as Error).message || "erro inesperado"}`);
    } finally {
      setBusy(false);
    }
  }

  // Desfaz uma mesclagem incompleta: devolve o cadastro "preso" para active.
  async function handleUnstick(member: MergeMember) {
    setBusy(true);

    try {
      const { error } = await supabase
        .from("members")
        .update({ status: "active", merged_into_id: null, merged_links: null })
        .eq("id", member.id);
      if (error) throw new Error(error.message);

      await logActivity("member_unmerge", {
        member_id: member.id,
        member_name: member.full_name,
        reason: "unstick_incomplete_merge",
      });

      onToast("success", `${member.full_name} voltou a ser ativa. Agora use "Unificar" para juntar corretamente.`);
      onRefresh();
    } catch (err) {
      onToast("error", `Falha ao desfazer: ${(err as Error).message || "erro inesperado"}`);
    } finally {
      setBusy(false);
    }
  }

  const hasAnything = groups.length > 0 || properMerged.length > 0 || stuckMerged.length > 0;

  return (
    <div className="mb-8">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 flex items-center justify-center rounded-full bg-primary/10">
            <i className="ri-git-merge-line text-primary" />
          </div>
          <div>
            <h3 className="font-display text-base text-brown">Cadastros duplicados</h3>
            <p className="font-body text-xs text-brown/50">
              {groups.length} grupo{groups.length === 1 ? "" : "s"} para resolver
              {stuckMerged.length > 0 ? ` · ${stuckMerged.length} mesclage${stuckMerged.length === 1 ? "m" : "ns"} incompleta${stuckMerged.length === 1 ? "" : "s"}` : ""}
              {properMerged.length > 0 ? ` · ${properMerged.length} mesclado${properMerged.length === 1 ? "" : "s"}` : ""}
            </p>
          </div>
        </div>
      </div>

      {!hasAnything ? (
        <div className="px-5 py-4 rounded-xl bg-green-50 border border-green-200 flex items-center gap-3">
          <div className="w-8 h-8 flex items-center justify-center rounded-full bg-green-100 shrink-0">
            <i className="ri-checkbox-circle-line text-green-600" />
          </div>
          <p className="font-body text-sm text-green-800">
            Nenhum cadastro duplicado encontrado por e-mail ou WhatsApp. Tudo certo!
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {/* Mesclagens incompletas (presas) */}
          {stuckMerged.length > 0 && (
            <div className="bg-amber-50 rounded-2xl p-4 md:p-5 border border-amber-200">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-7 h-7 flex items-center justify-center rounded-full bg-amber-100 shrink-0">
                  <i className="ri-alert-line text-amber-600" />
                </div>
                <p className="font-body text-sm font-semibold text-amber-800">
                  Mesclagens incompletas (cadastros presos)
                </p>
              </div>
              <p className="font-body text-xs text-amber-700/80 mb-4">
                Estes cadastros foram marcados como mesclados mas nunca foram ligados a um cadastro
                sobrevivente. Use <strong>Desfazer mesclagem</strong> para devolvê-los a ativos, ou
                resolva pelo grupo de <strong>Unificar</strong> abaixo.
              </p>
              <div className="space-y-2">
                {stuckMerged.map((m) => (
                  <div key={String(m.id)} className="bg-white rounded-xl px-4 py-3 flex items-center gap-3">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-amber-100 shrink-0">
                      <i className="ri-alert-line text-amber-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-body text-sm text-brown">
                        {m.full_name} <span className="text-brown/40">(ID {String(m.id)})</span>
                      </p>
                      <p className="font-body text-xs text-brown/50">
                        {linkSummary(m)} · {m.email ? m.email.trim() : "sem e-mail"}
                      </p>
                    </div>
                    <button
                      onClick={() => handleUnstick(m)}
                      disabled={busy}
                      className="px-3 py-1.5 rounded-lg bg-white border border-amber-300 text-amber-700 font-body text-xs hover:bg-amber-100 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                    >
                      Desfazer mesclagem
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Grupos de duplicados */}
          {groups.map((group, idx) => (
            <div key={idx} className="bg-white rounded-2xl p-4 md:p-5 border border-nude">
              <div className="flex flex-col md:flex-row md:items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <p className="font-body text-xs text-brown/40 uppercase tracking-wide">
                      Grupo {idx + 1} · {group.length} cadastros
                    </p>
                    {groupHasIncompleteMerge(group) && (
                      <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-body text-[10px] uppercase tracking-wide">
                        mesclagem incompleta
                      </span>
                    )}
                  </div>
                  <div className="space-y-1.5">
                    {group.map((m) => (
                      <div key={String(m.id)} className="flex items-center gap-2 flex-wrap">
                        <span className="font-body text-sm text-brown">{m.full_name}</span>
                        <span className="font-body text-xs text-brown/40">
                          (ID {String(m.id)} · {linkSummary(m)})
                        </span>
                        {m.photo && (
                          <span className="font-body text-[10px] text-primary">
                            <i className="ri-camera-line" /> tem foto
                          </span>
                        )}
                        {m.email && (
                          <span className="font-body text-xs text-brown/40 break-all">{m.email.trim()}</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                <button
                  onClick={() => openMerge(group)}
                  disabled={busy}
                  className="px-4 py-2 rounded-full bg-primary text-white font-body text-sm hover:bg-primary-dark transition-colors cursor-pointer whitespace-nowrap shrink-0 disabled:opacity-50"
                >
                  <i className="ri-git-merge-line mr-1" /> Unificar
                </button>
              </div>
            </div>
          ))}

          {/* Mesclagens já feitas (com desfazer) — recolhido por padrão */}
          {properMerged.length > 0 && (
            <div className="mt-5">
              <button
                onClick={() => setShowRecentMerges((v) => !v)}
                className="w-full flex items-center justify-between px-4 py-3 rounded-xl bg-white border border-nude cursor-pointer hover:bg-beige/40 transition-colors"
              >
                <span className="font-body text-xs text-brown/40 uppercase tracking-wide">
                  Mesclagens recentes ({properMerged.length})
                </span>
                <span className="w-5 h-5 flex items-center justify-center text-brown/40">
                  <i className={showRecentMerges ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} />
                </span>
              </button>

              {showRecentMerges && (
                <div className="space-y-2 mt-2">
                  {properMerged.map((m) => {
                    const target = members.find((x) => String(x.id) === String(m.merged_into_id));
                    const mergedAt = (m.merged_links as Record<string, unknown> | null)?.merged_at as string | undefined;
                    return (
                      <div key={String(m.id)} className="bg-white rounded-xl px-4 py-3 flex flex-col md:flex-row md:items-center gap-3">
                        <div className="w-8 h-8 flex items-center justify-center rounded-full bg-brown/10 shrink-0">
                          <i className="ri-arrow-go-back-line text-brown/50" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-body text-sm text-brown/60">
                            <span className="text-brown">{m.full_name}</span> foi mesclada em{" "}
                            <span className="text-brown">{target?.full_name || "cadastro removido"}</span>
                            {mergedAt && (
                              <span className="text-brown/40">{" "}· {new Date(mergedAt).toLocaleDateString("pt-BR")}</span>
                            )}
                          </p>
                          <p className="font-body text-xs text-brown/40 mt-1">
                            Guardado apenas como histórico de recuperação — não conta como membro.
                          </p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {m.merged_links && (m.merged_links as Record<string, unknown>).snapshot && (
                            <button
                              onClick={() => setViewingOriginal(m)}
                              className="px-3 py-1.5 rounded-lg bg-white border border-nude text-brown/60 font-body text-xs hover:bg-beige transition-colors cursor-pointer whitespace-nowrap"
                              title="Ver o retrato completo deste cadastro antes da mesclagem"
                            >
                              ver cadastro original
                            </button>
                          )}
                          <button
                            onClick={() => handleUndo(m)}
                            disabled={busy}
                            className="px-3 py-1.5 rounded-lg bg-white border border-nude text-brown font-body text-xs hover:bg-beige transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                          >
                            Desfazer mesclagem
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {activeGroup && (
        <MergeModal
          group={activeGroup}
          selection={selection}
          setSelection={setSelection}
          keptId={keptId}
          setKeptId={setKeptId}
          linkCounts={linkCounts}
          busy={busy}
          onConfirm={handleConfirmMerge}
          onClose={() => setActiveGroup(null)}
        />
      )}

      {viewingOriginal && (
        <OriginalSnapshotModal
          snapshot={
            (viewingOriginal.merged_links as Record<string, unknown> | null)?.snapshot as
              | Record<string, unknown>
              | undefined
          }
          onClose={() => setViewingOriginal(null)}
        />
      )}
    </div>
  );
});

export default MemberMergeTool;